import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Tooltip,
} from "@mui/material";
import Table from "components/Table";
import { useEffect, useState, useMemo, useCallback, useContext } from "react";
import { createSearchParams, Link, useLocation, UNSAFE_NavigationContext } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import "./TableContainer.scss";
import { APP_GOOGLE_SEM_API_ENDPOINT } from "constants/api_urls";
import { getUserToken } from "../../../../utils/functions/userToken";
import { updateHotelId } from "store/slices/account/accountSlice";
import qs from "query-string";
import { KEYWORD_TYPE_OPTIONS, MATCH_TYPE_OPTIONS } from "../utils";
import KeywordsMatchTypeDropdown from "../KeywordsMatchTypeDropdown";
import EditIcon from "../../../../assets/images/ActionIcons/Edit.svg";
import { format } from "date-fns";
import { enqueueSnackbar } from "notistack";
import KeywordText from "../KeywordText";
import CustomOutlinedInput from "components/CustomOutlinedInput/CustomOutlinedInput";
import ConfirmationDialog from "components/ConfirmationDialog";

const KEYWORDS_FILTER_OPTIONS = [
  {
    label: "Keywords",
    value: "positive_keyword",
  },
  {
    label: "Search Terms",
    value: "search_term",
  },
  {
    label: "Negative Keywords",
    value: "negative_keyword",
  },
];

const endpoints = {
  search_term: `${APP_GOOGLE_SEM_API_ENDPOINT}/api/google-sem/keywords/table/search-terms/`,
  negative_keyword: `${APP_GOOGLE_SEM_API_ENDPOINT}/api/google-sem/keywords/table/negative/`,
  positive_keyword: `${APP_GOOGLE_SEM_API_ENDPOINT}/api/google-sem/keywords/table/positive/`,
};

const bulkEditEndpoints = {
  positive_keyword: `${APP_GOOGLE_SEM_API_ENDPOINT}/api/google-sem/keywords/table/positive/edit`,
  search_term: `${APP_GOOGLE_SEM_API_ENDPOINT}/api/google-sem/keywords/create-from-search-term`,
  negative_keyword: `${APP_GOOGLE_SEM_API_ENDPOINT}/api/google-sem/keywords/table/negative/edit`,
};

const KeywordsTableContainer = ({ onEditCampaign }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const searchParams = qs.parse(window.location.search);
  const [apiData, setApiData] = useState(null);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(false);
  const [paginationObject, setPaginationObject] = useState({});
  const [pageSize, setPageSize] = useState(15);
  const [pageIndex, setPageIndex] = useState(0);
  const [sortingState, setSortingState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableType, setTableType] = useState("positive_keyword");
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [editRowPayload, setEditRowPayload] = useState({});
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const [bulkEditPayload, setBulkEditPayload] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [pendingTabSwitch, setPendingTabSwitch] = useState(null);

  const hasUnsavedChanges = isBulkEditMode && Object.keys(bulkEditPayload).length > 0;

  // Block browser close / refresh
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  // Block in-app React Router navigation (works with BrowserRouter)
  const { navigator } = useContext(UNSAFE_NavigationContext);
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    navigator.push = (...args) => {
      if (window.confirm("You have unsaved changes. Are you sure you want to leave? Your changes will be lost.")) {
        originalPush.apply(navigator, args);
      }
    };
    navigator.replace = (...args) => {
      if (window.confirm("You have unsaved changes. Are you sure you want to leave? Your changes will be lost.")) {
        originalReplace.apply(navigator, args);
      }
    };

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [hasUnsavedChanges, navigator]);

  const token = getUserToken() || "";
  const { account_id = "" } = useSelector(
    (state) => state?.account?.account || {},
  );
  const { hotel_id = [], level } = useSelector((state) => state?.account);
  const {
    currentDateRange = {},
    comparitionDateRange = {},
    compareWith,
  } = useSelector((state) => state?.calender);
  const { campaign = [] } = useSelector((state) => state?.searchAds);

  // --- Bulk edit helpers ---
  const handleBulkFieldChange = useCallback((keywordId, field, value, row, currentTableType) => {
    setBulkEditPayload((prev) => {
      if (prev[keywordId]) {
        return {
          ...prev,
          [keywordId]: { ...prev[keywordId], [field]: value },
        };
      }

      // Seed defaults based on table type
      let defaults;
      if (currentTableType === "search_term") {
        defaults = {
          keyword_text: row?.search_term ?? row?.keyword_text,
          match_type: row?.search_term_match_type ?? row?.match_type,
          keyword_type: row?.add_as ?? "positive",
          keyword_id: row?.keyword_id ?? row?.id,
          ad_group_id: row?.ad_group_id,
        };
      } else if (currentTableType === "negative_keyword") {
        defaults = {
          keyword_id: keywordId,
          keyword_text: row?.keyword_text,
          match_type: row?.match_type,
        };
      } else {
        defaults = {
          keyword_id: keywordId,
          keyword_text: row?.keyword_text,
          match_type: row?.match_type,
          keywords_status: row?.keywords_status ?? null,
          max_cpc: row?.bidding_max_value ?? null,
        };
      }

      return {
        ...prev,
        [keywordId]: { ...defaults, [field]: value },
      };
    });
  }, []);

  const handleBulkEditToggle = () => {
    setIsBulkEditMode(true);
    setBulkEditPayload({});
    setSelectedRowId(null);
    setEditRowPayload({});
  };

  const handleBulkCancel = () => {
    setIsBulkEditMode(false);
    setBulkEditPayload({});
  };

  const handleBulkSave = () => {
    const changedKeywords = Object.values(bulkEditPayload);
    if (changedKeywords.length === 0) {
      enqueueSnackbar("No changes to save", { variant: "info" });
      return;
    }

    let payload;
    let method;

    if (tableType === "positive_keyword") {
      payload = {
        keywords: changedKeywords.map((kw) => ({
          keyword_id: kw.keyword_id,
          keyword_text: kw.keyword_text,
          match_type: kw.match_type,
          ...(kw.keywords_status != null ? { keywords_status: kw.keywords_status } : {}),
          ...(kw.max_cpc != null ? { max_cpc: parseFloat(kw.max_cpc) || null } : {}),
        })),
      };
      method = axios.put;
    } else if (tableType === "search_term") {
      payload = {
        keywords: changedKeywords.map((kw) => ({
          keyword_text: kw.keyword_text,
          match_type: kw.match_type,
          keyword_type: kw.keyword_type,
          keyword_id: kw.keyword_id,
          ...(kw.ad_group_id != null ? { ad_group_id: kw.ad_group_id } : {}),
        })),
      };
      method = axios.post;
    } else {
      // negative_keyword
      payload = {
        keywords: changedKeywords.map((kw) => ({
          keyword_id: kw.keyword_id,
          keyword_text: kw.keyword_text,
          match_type: kw.match_type,
        })),
      };
      method = axios.put;
    }

    setBulkSaving(true);
    method(bulkEditEndpoints[tableType], payload, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(() => {
        setTableData((prevData) =>
          prevData.map((row) => {
            const change = bulkEditPayload[row.id];
            if (!change) return row;

            if (tableType === "positive_keyword") {
              return {
                ...row,
                keyword_text: change.keyword_text ?? row.keyword_text,
                match_type: change.match_type ?? row.match_type,
                keywords_status: change.keywords_status ?? row.keywords_status,
                max_cpc: change.max_cpc ?? row.max_cpc,
                bidding_max_value: change.max_cpc ?? row.bidding_max_value,
              };
            } else if (tableType === "search_term") {
              return {
                ...row,
                search_term: change.keyword_text ?? row.search_term,
                search_term_match_type: change.match_type ?? row.search_term_match_type,
                add_as: change.keyword_type ?? row.add_as,
              };
            } else {
              return {
                ...row,
                keyword_text: change.keyword_text ?? row.keyword_text,
                match_type: change.match_type ?? row.match_type,
              };
            }
          }),
        );
        enqueueSnackbar("Keywords updated successfully", { variant: "success" });
        setIsBulkEditMode(false);
        setBulkEditPayload({});
      })
      .catch((error) => {
        enqueueSnackbar(
          error?.response?.data?.[0]?.errorMessage || "Failed to update keywords",
          { variant: "error" },
        );
      })
      .finally(() => {
        setBulkSaving(false);
      });
  };

  // --- Table filter options with Edit / Save / Cancel ---
  const tableFilterOptions = (
    <Box sx={{ display: "flex", gap: "10px", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
        {KEYWORDS_FILTER_OPTIONS.map((option) => (
          <Tooltip key={option.value} title={option.label}>
            <Chip
              label={option.label}
              sx={{
                background: tableType === option.value ? "#61D5A9" : "inherit",
                cursor: "pointer",
              }}
              onClick={() => {
                if (hasUnsavedChanges && option.value !== tableType) {
                  setPendingTabSwitch(option.value);
                  return;
                }
                setTableType(option.value);
                setIsBulkEditMode(false);
                setBulkEditPayload({});
              }}
            />
          </Tooltip>
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: "8px", alignItems: "center", marginLeft: "auto", marginRight: "4px" }}>
        {!isBulkEditMode ? (
          <Button
            variant="contained"
            size="small"
            onClick={handleBulkEditToggle}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              backgroundColor: "#61D5A9",
              color: "#fff",
              "&:hover": { backgroundColor: "#12794F" },
            }}
          >
            Edit
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              size="small"
              onClick={handleBulkSave}
              disabled={bulkSaving || Object.keys(bulkEditPayload).length === 0}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                backgroundColor: "#61D5A9",
                color: "#fff",
                "&:hover": { backgroundColor: "#12794F" },
                "&:disabled": { backgroundColor: "#c8e6d8", color: "#fff" },
              }}
            >
              {bulkSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleBulkCancel}
              disabled={bulkSaving}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                borderColor: "#ccc",
                color: "#666",
                "&:hover": { borderColor: "#999", backgroundColor: "#fafafa" },
              }}
            >
              Cancel
            </Button>
          </>
        )}
      </Box>
    </Box>
  );


  useEffect(() => {
    const params = {
      account_id,
      level,
      "hotel_id[]": hotel_id,
      start_date: format(
        currentDateRange?.startDate || new Date(),
        "yyyy-MM-dd",
      ),
      end_date: format(currentDateRange?.endDate || new Date(), "yyyy-MM-dd"),
      ...(comparitionDateRange?.startDate && comparitionDateRange?.endDate
        ? {
            compare_start_date: format(
              comparitionDateRange?.startDate,
              "yyyy-MM-dd",
            ),
            compare_end_date: format(
              comparitionDateRange?.endDate,
              "yyyy-MM-dd",
            ),
          }
        : {}),
      "sort_asc[]": sortingState
        ?.filter((sort) => !sort?.desc)
        ?.map((s) => s?.id),
      "sort_desc[]": sortingState
        ?.filter((sort) => sort?.desc)
        ?.map((s) => s?.id),
      page_number: pageIndex,
      number_of_rows: pageSize,
    };

    if (account_id && level) {
      setLoading(true);
      axios
        .post(endpoints[tableType], createSearchParams(params), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          const data = res?.data;
          setIsPaginationEnabled(data?.paginator_enabled);
          setPaginationObject(data);
          setApiData(data);
          setVisibleColumnKeys(null);
          
          const rowsToDisplay = data?.row_list?.map((row) => ({
            id: row?.keyword_id,
            ...row,
          }));
          
          setTableData([...rowsToDisplay]);
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(campaign),
    token,
    account_id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(currentDateRange),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(comparitionDateRange),
    level,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(sortingState),
    pageSize,
    pageIndex,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(hotel_id),
    tableType
  ]);

  const columns = useMemo(() => {
    if (!apiData?.columns) return [];
    
    const otherColumns = apiData?.columns?.filter((col) => col?.id !== "action");
    
    const sortedColumns = [
      ...otherColumns?.filter(
        (col) => col?.type !== "metrics" && col?.type === "segment",
      ),
      ...otherColumns?.filter((col) => col?.type === "metrics"),
      ...otherColumns?.filter(
        (col) => col?.type !== "metrics" && col?.type !== "segment",
      ),
    ];
    
    const mappedColumns = sortedColumns?.map((col, index) => {
            let renderCell = ({ row, cell }) => {
              let style = {};
              if (row.id === "0" && cell.column.id === "0") {
                style = {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "12px",
                  fontWeight: "600",
                };
              }
              return compareWith && col?.type !== "segment" ? (
                <Tooltip
                  title={`${
                    row?.original?.[
                      `${cell.column?.columnDef?.accessorKey}_compare`
                    ]
                  } ${row?.original?.[`${cell.column?.columnDef?.accessorKey}_compare_percent`]}%`}
                >
                  <span style={style}>{cell.getValue()}</span>
                </Tooltip>
              ) : (
                <span style={style}>{cell.getValue()}</span>
              );
            };

            // --- match_type / keywords_status ---
            if (["match_type", "keywords_status"].includes(col?.id)) {
              renderCell = ({ row, cell }) => {
                // Search term tab: show match type as read-only text when not in bulk edit
                if (tableType === "search_term" && !isBulkEditMode) {
                  return <span>{row?.original?.search_term_match_type}</span>;
                }

                // Bulk edit mode for match_type across all table types
                if (isBulkEditMode && col?.id === "match_type" && row?.original?.id) {
                  const currentValue =
                    bulkEditPayload[row.original.id]?.match_type
                    ?? (tableType === "search_term" ? row.original?.search_term_match_type : cell.getValue());
                  return (
                    <KeywordsMatchTypeDropdown
                      options={MATCH_TYPE_OPTIONS}
                      value={String(currentValue)}
                      onChange={(newValue) => {
                        handleBulkFieldChange(
                          row.original.id,
                          "match_type",
                          newValue,
                          row.original,
                          tableType,
                        );
                      }}
                    />
                  );
                }

                // Bulk edit mode for keywords_status (positive_keyword only)
                if (isBulkEditMode && col?.id === "keywords_status" && tableType === "positive_keyword" && row?.original?.id) {
                  const currentValue =
                    bulkEditPayload[row.original.id]?.keywords_status ?? cell.getValue();
                  return (
                    <KeywordsMatchTypeDropdown
                      options={apiData?.positive_keyword_status_options || []}
                      value={String(currentValue)}
                      onChange={(newValue) => {
                        handleBulkFieldChange(
                          row.original.id,
                          "keywords_status",
                          newValue,
                          row.original,
                          tableType,
                        );
                      }}
                    />
                  );
                }

                // Single-row edit mode
                if (row?.original?.id !== selectedRowId) {
                  return <span>{cell.getValue()}</span>;
                }

                const options =
                  col?.id === "match_type"
                    ? MATCH_TYPE_OPTIONS
                    : apiData?.positive_keyword_status_options || [];
                return (
                  <KeywordsMatchTypeDropdown
                    options={options}
                    value={String(cell.getValue())}
                    onChange={(newValue) => {
                      setEditRowPayload((prev) => ({
                        ...prev,
                        keyword_text: prev?.keyword_text || row?.original?.keyword_text,
                        match_type: col?.id === "match_type" ? newValue : (prev.match_type || row?.original?.match_type),
                        keywords_status: col?.id === "keywords_status" ? newValue : (prev.keywords_status || row?.original?.keywords_status),
                      }));
                    }}
                  />
                );
              };
            }

            // --- add_as (search_term tab) ---
            if (col.id === "add_as") {
              renderCell = ({ row, cell }) => {
                // Bulk edit mode
                if (isBulkEditMode && row?.original?.id) {
                  const currentValue =
                    bulkEditPayload[row.original.id]?.keyword_type ?? cell.getValue() ?? "";
                  return (
                    <KeywordsMatchTypeDropdown
                      options={KEYWORD_TYPE_OPTIONS}
                      value={String(currentValue)}
                      onChange={(newValue) => {
                        handleBulkFieldChange(
                          row.original.id,
                          "keyword_type",
                          newValue,
                          row.original,
                          tableType,
                        );
                      }}
                    />
                  );
                }

                // Single-row edit mode
                if (row?.original?.id !== selectedRowId) {
                  return <span>{cell.getValue() ? cell.getValue() === "positive" ? "New Keyword" : "Negative" : ""}</span>;
                }

                return (
                  <KeywordsMatchTypeDropdown
                    options={KEYWORD_TYPE_OPTIONS}
                    value={String(cell.getValue() || "")}
                    onChange={(newValue) => {
                      setEditRowPayload((prev) => ({
                        ...prev,
                        keyword_text: prev?.keyword_text || row?.original?.keyword_text,
                        match_type: prev.match_type || row?.original?.search_term_match_type,
                        keyword_type: col?.id === "add_as" ? newValue : (prev.keyword_type || row?.original?.add_as),
                        ad_group_id: col?.id === "add_as" ? row?.original?.ad_group_id : undefined,
                      }));
                    }}
                  />
                );
              };
            }

            // --- search_term text ---
            if (col.id === "search_term") {
              renderCell = ({ row, cell }) => {
                // Bulk edit mode
                if (isBulkEditMode && row?.original?.id) {
                  const currentValue =
                    bulkEditPayload[row.original.id]?.keyword_text ?? cell.getValue();
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <KeywordText
                        value={currentValue}
                        onBlur={(e) => {
                          const newValue = e.target.value;
                          if (newValue !== cell.getValue()) {
                            handleBulkFieldChange(
                              row.original.id,
                              "keyword_text",
                              newValue,
                              row.original,
                              tableType,
                            );
                          }
                        }}
                      />
                    </div>
                  );
                }

                // Single-row edit mode
                if (row?.original?.id !== selectedRowId) {
                  return <span>{cell.getValue()}</span>;
                }
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <KeywordText
                      value={cell.getValue()}
                      onBlur={(e) => {
                        const newValue = e.target.value;
                        if (newValue !== cell.getValue()) {
                          setEditRowPayload((prev) => ({
                            ...prev,
                            match_type: prev.match_type || row?.original?.search_term_match_type,
                            keyword_text: newValue,
                            keyword_type: prev?.keyword_type || row?.original?.add_as,
                          }));
                        }
                      }}
                    />
                  </div>
                );
              };
            }

            // --- bidding_max_value (positive_keyword only) ---
            if (col.id === "bidding_max_value") {
              renderCell = ({ row, cell }) => {
                if (isBulkEditMode && row?.original?.id) {
                  const currentValue =
                    bulkEditPayload[row.original.id]?.max_cpc ?? row.original?.bidding_max_value ?? "";
                  return (
                    <CustomOutlinedInput
                      value={currentValue}
                      placeholder="Max CPC"
                      size="small"
                      type="number"
                      className="no-border-focus"
                      inputProps={{ step: "0.01", min: "0" }}
                      onChange={(e) => {
                        handleBulkFieldChange(
                          row.original.id,
                          "max_cpc",
                          e.target.value,
                          row.original,
                          tableType,
                        );
                      }}
                      sx={{ width: "90px", fontSize: "12px" }}
                    />
                  );
                }

                return (
                  <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    {cell.getValue() ? `${cell.getValue()}` : "0"}
                  </span>
                );
              };
            }

            // --- keyword_text (positive_keyword & negative_keyword) ---
            if (col.id === "keyword_text") {
              renderCell = ({ row, cell }) => {
                if (isBulkEditMode && row?.original?.id) {
                  const currentValue =
                    bulkEditPayload[row.original.id]?.keyword_text ?? cell.getValue();
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <KeywordText
                        value={currentValue}
                        onBlur={(e) => {
                          const newValue = e.target.value;
                          if (newValue !== cell.getValue()) {
                            handleBulkFieldChange(
                              row.original.id,
                              "keyword_text",
                              newValue,
                              row.original,
                              tableType,
                            );
                          }
                        }}
                      />
                    </div>
                  );
                }

                if (row?.original?.id !== selectedRowId) {
                  return <span>{cell.getValue()}</span>;
                }
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <KeywordText
                      value={cell.getValue()}
                      onBlur={(e) => {
                        const newValue = e.target.value;
                        if (newValue !== cell.getValue()) {
                          setEditRowPayload((prev) => ({
                            ...prev,
                            match_type: prev.match_type || row?.original?.match_type,
                            keywords_status: prev.keywords_status || row?.original?.keywords_status,
                            keyword_text: newValue,
                          }));
                        }
                      }}
                    />
                  </div>
                );
              };
            }

            // --- hotel_id link ---
            if (col?.id === "hotel_id") {
              renderCell = ({ row, cell }) => {
                return row?.id !== "0" ? (
                  <Link
                    to={`${pathname}?${createSearchParams({
                      ...searchParams,
                      hotelid: row?.original?.hotel_id_id,
                    })}`}
                    style={{
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                    onClick={() => {
                      dispatch(
                        updateHotelId([Number(row?.original?.hotel_id_id)]),
                      );
                    }}
                  >
                    <span className="clickable-element">{cell.getValue()}</span>
                  </Link>
                ) : (
                  <span>
                    {cell.getValue()}
                  </span>
                );
              };
            }

          return {
            id: index.toString(),
            accessorKey: col.id,
            Header: (
              <Tooltip title={col?.description}>
                <span>{col?.label}</span>
              </Tooltip>
            ),
            header: col?.label,
            Cell: renderCell,
            isSelected: col?.is_visible !== undefined ? col?.is_visible : true,
            type: col?.type,
            isVisible: col?.is_visible !== undefined ? col?.is_visible : true,
          };
        });

    return mappedColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData, selectedRowId, compareWith, pathname, searchParams, dispatch, tableType, isBulkEditMode, bulkEditPayload]);
  
  const selectedColumns = useMemo(() => {
    if (!columns.length) return [];
    if (visibleColumnKeys === null) {
      return columns
        .filter((col) => col?.type === "metrics" ? col?.isVisible : true)
        .map((col) => ({ ...col, isSelected: true }));
    }
    return columns
      .filter((col) => visibleColumnKeys.has(col.accessorKey))
      .map((col) => ({ ...col, isSelected: true }));
  }, [columns, visibleColumnKeys]);

  return (
    <>
      <Grid sx={{ marginBlock: "10px", maxWidth: "100%" }}>
        <Table
          page="searchAdsKeywordsManagement"
          rows={tableData}
          columns={columns}
          selectedColumns={selectedColumns}
          handleColumnSelectionChange={(selCols) => {
            const nonMetricKeys = columns
              .filter((col) => !["metrics", "METRICS"].includes(col?.type))
              .map((col) => col.accessorKey);
            const selectedMetricKeys = selCols.map((c) => c.accessorKey);
            setVisibleColumnKeys(new Set([...nonMetricKeys, ...selectedMetricKeys]));
          }}
          maxHeight={"calc(100vh - 255px)"}
          paginationData={paginationObject}
          isPaginationEnabled={isPaginationEnabled}
          handlePagination={(pagination) => {
            setPageIndex(pagination?.pageIndex);
            setPageSize(pagination?.pageSize);
          }}
          initialPageSize={pageSize}
          initialPageIndex={pageIndex}
          handleSorting={(sorting) => {
            setSortingState(sorting);
          }}
          isLoading={loading}
          showTotal={false}
          tableFilterOptions={tableFilterOptions}
          isColumnSelection={tableType !== "negative_keyword"}
          onEditRowClick={(id) => {
            if (isBulkEditMode) return;
            const row = tableData.find((r) => r.id === id);
            setSelectedRowId(id);
            setEditRowPayload({
              keyword_text: row?.keyword_text || '',
              match_type: row?.match_type || row?.search_term_match_type || '',
              keywords_status: row?.keywords_status || '',
              keyword_type: row?.add_as || '',
              ...(tableType === 'search_term' ? { keyword_id: row?.keyword_id ?? row?.id, ad_group_id: row?.ad_group_id } : {}),
            });
          }}
          onSaveRowClick={(id) => {
            const saveEndpoint = tableType === 'search_term' ? `${APP_GOOGLE_SEM_API_ENDPOINT}/api/google-sem/keywords/create-from-search-term` : `${endpoints[tableType]}edit/${id}`;
            const callMethod = tableType === 'search_term' ? axios.post : axios.put;
            const savePayload = tableType === 'search_term'
              ? {
                  keywords: [{
                    keyword_text: editRowPayload.keyword_text,
                    match_type: editRowPayload.match_type,
                    keyword_type: editRowPayload.keyword_type,
                    keyword_id: editRowPayload.keyword_id,
                    ...(editRowPayload.ad_group_id != null ? { ad_group_id: editRowPayload.ad_group_id } : {}),
                  }],
                }
              : editRowPayload;
            callMethod(saveEndpoint, savePayload, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
              .then(() => {
                setTableData(prevData => 
                  prevData.map(row => 
                    row.id === id
                      ? {
                          ...row,
                          keyword_text: editRowPayload.keyword_text || row.keyword_text,
                          match_type: editRowPayload.match_type || row.match_type,
                          keywords_status: editRowPayload.keywords_status || row.keywords_status,
                          add_as: editRowPayload.keyword_type || row.add_as,
                        }
                      : row
                  )
                );

                enqueueSnackbar("Keyword updated successfully", { variant: "success" });
                setSelectedRowId(null);
                setEditRowPayload({});
              })
              .catch((error) => {
                enqueueSnackbar(
                  error?.response?.data?.[0]?.errorMessage || "Failed to update keyword",
                  { variant: "error" }
                );
              });
          }}
          onCancelRowClick={() => {
            setSelectedRowId(null);
            setEditRowPayload({});
          }}
          selectedRowId={selectedRowId}
          tableType={tableType}
        />
      </Grid>

      <ConfirmationDialog
        open={pendingTabSwitch !== null}
        onClose={() => setPendingTabSwitch(null)}
        onConfirm={() => {
          const nextTab = pendingTabSwitch;
          setPendingTabSwitch(null);
          setIsBulkEditMode(false);
          setBulkEditPayload({});
          setTableType(nextTab);
        }}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
      />
    </>
  );
};

export default KeywordsTableContainer;
